import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';

export default function IpbmRate() {
  const api = useApi();
  const { user } = useAuth();

  const [rates,       setRates]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [editing,     setEditing]     = useState(false);
  const [draft,       setDraft]       = useState([]);
  const [saving,      setSaving]      = useState(false);

  const canEdit = (user?.accesslevel ?? 0) >= 4;

  const load = useCallback(async () => {
    setLoading(true);
    try { setRates(await api.get('/reports/invoice/rates')); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleEdit = () => {
    setDraft(rates.map(r => ({ ...r })));
    setEditing(true);
  };

  const handleCancel = () => setEditing(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/reports/invoice/rates', { rates: draft });
      setRates(draft);
      setEditing(false);
    } catch (e) { alert('Failed to save rates: ' + e.message); }
    finally { setSaving(false); }
  };

  const updateDraft = (i, field, value) => {
    const d = [...draft];
    d[i] = { ...d[i], [field]: value };
    setDraft(d);
  };

  const addRow = () => {
    setDraft(d => [...d, {
      min_transactions: 0,
      max_transactions: null,
      rate_per_claim:   0,
      label:            '',
    }]);
  };

  const removeRow = (i) => {
    setDraft(d => d.filter((_, idx) => idx !== i));
  };

  const displayRates = editing ? draft : rates;

  return (
    <div className="animate-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">IPBM Rate</h1>
          <div className="page-subtitle">Transaction fee rate tiers for IPBM invoicing</div>
        </div>
        {canEdit && !editing && (
          <button className="btn btn-primary" onClick={handleEdit}>Edit Rates</button>
        )}
        {editing && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={handleCancel}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* Info card */}
      <div className="card" style={{ marginBottom: 20, padding: '14px 20px' }}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Destination</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent)', fontSize: 14 }}>IPBM</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Billing Rule</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Count(DISTINCT industryno + refno) per month · txtype = 1 · claim &gt; 0
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Rate Applied</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Based on total monthly transaction volume — rate applies to all transactions
            </div>
          </div>
        </div>
      </div>

      {/* Rate tiers table */}
      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Volume Band Label</th>
                  <th style={{ textAlign: 'right' }}>Min Transactions</th>
                  <th style={{ textAlign: 'right' }}>Max Transactions</th>
                  <th style={{ textAlign: 'right' }}>Rate per Claim (R)</th>
                  {editing && <th style={{ width: 60 }}></th>}
                </tr>
              </thead>
              <tbody>
                {displayRates.map((r, i) => (
                  <tr key={i}>
                    <td>
                      {editing ? (
                        <input className="form-input" value={r.label}
                          onChange={e => updateDraft(i, 'label', e.target.value)}
                          placeholder="e.g. < 250 000"
                          style={{ width: '100%', fontSize: 12 }} />
                      ) : r.label || '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                      {editing ? (
                        <input className="form-input" type="number" value={r.min_transactions}
                          onChange={e => updateDraft(i, 'min_transactions', Number(e.target.value))}
                          style={{ width: 120, textAlign: 'right', fontSize: 12 }} />
                      ) : Number(r.min_transactions).toLocaleString()}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                      {editing ? (
                        <input className="form-input" type="number" value={r.max_transactions ?? ''}
                          placeholder="no limit (∞)"
                          onChange={e => updateDraft(i, 'max_transactions', e.target.value === '' ? null : Number(e.target.value))}
                          style={{ width: 120, textAlign: 'right', fontSize: 12 }} />
                      ) : r.max_transactions ? Number(r.max_transactions).toLocaleString() : '∞'}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                      {editing ? (
                        <input className="form-input" type="number" step="0.0001" value={r.rate_per_claim}
                          onChange={e => updateDraft(i, 'rate_per_claim', Number(e.target.value))}
                          style={{ width: 100, textAlign: 'right', fontSize: 12 }} />
                      ) : (
                        <span style={{ color: 'var(--green)', fontWeight: 600 }}>
                          R {Number(r.rate_per_claim).toFixed(4)}
                        </span>
                      )}
                    </td>
                    {editing && (
                      <td>
                        <button className="btn btn-danger btn-sm" onClick={() => removeRow(i)}>✕</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {editing && (
              <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)' }}>
                <button className="btn btn-ghost btn-sm" onClick={addRow}>+ Add Tier</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Usage note */}
      {!editing && rates.length > 0 && (
        <div style={{ marginTop: 16, fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          * The applicable rate is determined by the total transaction count for the invoice month.
          The rate matching the volume band is applied to all transactions — not a tiered/stepped calculation.
        </div>
      )}
    </div>
  );
}
